import { Tooltip } from '@/components/ui/tooltip';
import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import { SelectContent, SelectItem, SelectRoot, SelectTrigger } from '@/components/ui/select';
import { Badge, createListCollection, HStack, Icon, Text } from '@chakra-ui/react';
import type { TemplateLibraryEntry } from '@fluenceur/types';
import { Attachment, Post } from 'iconoir-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageFlag } from '@/components/language/LanguageFlag';
import { isFluenceurTemplate } from './template-selection';
import { templateNameLabelKey } from './template-library-utils';

interface TemplatePickerItem {
  value: string;
  label: string;
  language: string;
  isFluenceur: boolean;
  hasAttachments: boolean;
}

/** Same tag as the system rows of the template library table. */
const FluenceurTemplateBadge = () => {
  const { t } = useTranslation();
  return (
    <Badge size="xs" colorPalette="gray" variant="subtle" flexShrink={0}>
      {t('templates.badges.fluenceur')}
    </Badge>
  );
};

interface TemplatePickerProps {
  entries: TemplateLibraryEntry[];
  selectedEntryId: string | null;
  onSelect: (entryId: string) => void;
  disabled?: boolean;
  /** When attached to a control on its left (e.g. the save-as-template button),
   *  squares the leading corners and overlaps the shared border. */
  attachedStart?: boolean;
  confirmAttachmentReplacement?: boolean;
}

/**
 * Compact, non-native picker of the brand's own templates for the active
 * (channel, context) pair, followed by Fluenceur's, which carry a tag. Designed to sit in an editor footer, across from the
 * "insert variable" button. Selecting one reloads the editor body upstream.
 * Stays visible in an empty disabled state so availability issues do not look
 * like a missing control.
 */
const TemplatePicker = memo<TemplatePickerProps>(
  ({
    entries,
    selectedEntryId,
    onSelect,
    disabled,
    attachedStart,
    confirmAttachmentReplacement,
  }) => {
    const { t } = useTranslation();
    const [pendingEntryId, setPendingEntryId] = useState<string | null>(null);
    const hasEntries = entries.length > 0;
    const selectedEntry = useMemo(
      () => entries.find((entry) => entry.id === selectedEntryId) ?? null,
      [entries, selectedEntryId]
    );
    // A Fluenceur default has no stored name: it reads as its context.
    const displayName = useCallback(
      (entry: TemplateLibraryEntry) => {
        const labelKey = templateNameLabelKey(entry);
        return labelKey ? t(labelKey) : entry.name;
      },
      [t]
    );

    const collection = useMemo(
      () =>
        createListCollection<TemplatePickerItem>({
          items: entries.map((entry) => ({
            value: entry.id,
            label: displayName(entry),
            language: entry.language,
            isFluenceur: isFluenceurTemplate(entry),
            hasAttachments: !!entry.attachments?.length,
          })),
        }),
      [entries, displayName]
    );

    const selectedValue = useMemo(
      () => (selectedEntryId ? [selectedEntryId] : []),
      [selectedEntryId]
    );

    const handleValueChange = useCallback(
      (event: { value: string[] }) => {
        const next = event.value[0];
        if (!next) return;
        if (confirmAttachmentReplacement) setPendingEntryId(next);
        else onSelect(next);
      },
      [onSelect, confirmAttachmentReplacement]
    );

    const attachedTriggerCss = useMemo(
      () => (attachedStart ? { '& [data-part="trigger"]': { borderStartRadius: '0' } } : undefined),
      [attachedStart]
    );

    return (
      <>
        <SelectRoot
          collection={collection}
          value={selectedValue}
          onValueChange={handleValueChange}
          size="xs"
          w={{ base: '220px', md: '280px' }}
          minW="0"
          maxW={{ base: 'min(62vw, 260px)', md: '340px' }}
          disabled={disabled || !hasEntries}
          // Attached to the save button on its left: square the trigger's leading
          // corners (the border lives on the trigger slot, nested under Control).
          css={attachedTriggerCss}
        >
          <SelectTrigger>
            <HStack gap={2} minW={0} flex="1" justifyContent="space-between" pe={5}>
              <HStack gap={2} minW={0}>
                <Post width={14} height={14} strokeWidth={1.8} style={{ flexShrink: 0 }} />
                {selectedEntry ? (
                  <LanguageFlag language={selectedEntry.language} size={14} />
                ) : null}
                <Text
                  as="span"
                  truncate
                  color={selectedEntry ? 'fg' : hasEntries ? 'fg.muted' : 'fg.subtle'}
                  fontWeight={selectedEntry ? 'medium' : 'normal'}
                  minW={0}
                  textAlign="left"
                >
                  {(selectedEntry && displayName(selectedEntry)) ??
                    (hasEntries ? t('templates.picker.label') : t('templates.picker.empty'))}
                </Text>
                {selectedEntry && isFluenceurTemplate(selectedEntry) ? (
                  <FluenceurTemplateBadge />
                ) : null}
              </HStack>
              {!!selectedEntry?.attachments?.length && (
                <Tooltip content={t('templates.badges.attachments')}>
                  <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                    <Icon
                      asChild
                      boxSize="12px"
                      aria-hidden={false}
                      aria-label={t('templates.badges.attachments')}
                    >
                      <Attachment />
                    </Icon>
                  </span>
                </Tooltip>
              )}
            </HStack>
          </SelectTrigger>
          {/* Render the dropdown inline (no portal) so it stays inside the send
            modal's stacking context. The shared dialog (@/components/ui/dialog)
            pins its positioner at z-index 10000; a body-portaled select content
            opens behind it. Mirrors Chakra's "select within dialog" guidance. */}
          <SelectContent portalled={false}>
            {collection.items.map((item) => (
              <SelectItem
                key={item.value}
                item={item}
                pe={8}
                css={{
                  '& [data-part="item-indicator"]': { position: 'absolute', insetEnd: 2 },
                }}
              >
                <HStack gap={2} minW={0} flex="1" justifyContent="space-between">
                  <HStack gap={2} minW={0}>
                    <LanguageFlag language={item.language} size={14} />
                    <Text truncate minW={0}>
                      {item.label}
                    </Text>
                    {item.isFluenceur ? <FluenceurTemplateBadge /> : null}
                  </HStack>
                  {item.hasAttachments && (
                    <Tooltip content={t('templates.badges.attachments')}>
                      <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                        <Icon
                          asChild
                          boxSize="12px"
                          aria-hidden={false}
                          aria-label={t('templates.badges.attachments')}
                        >
                          <Attachment />
                        </Icon>
                      </span>
                    </Tooltip>
                  )}
                </HStack>
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
        <ConfirmDialog
          isOpen={!!pendingEntryId}
          title={t('templates.attachments.replaceTitle')}
          description={t('templates.attachments.replaceDescription')}
          confirmText={t('templates.attachments.replaceConfirm')}
          onCancel={() => setPendingEntryId(null)}
          onConfirm={() => {
            if (pendingEntryId) onSelect(pendingEntryId);
            setPendingEntryId(null);
          }}
        />
      </>
    );
  }
);

TemplatePicker.displayName = 'TemplatePicker';

export default TemplatePicker;
